/**
 * Verification mode preference, persisted to
 * `emlx:verification-mode`. Default `"parse-only"`.
 *
 * All four modes are now functional.
 *  - parse-only: nothing leaves the device (default).
 *  - dns-enabled: opt-in DoH lookups for SPF / DKIM / DMARC / MX / PTR
 *  (see `src/verification/dns-worker.ts`).
 *  - crypto-enabled: in-browser DKIM body-hash + signature verification
 *  plus S/MIME / OpenPGP signature surfaces (see
 *  `src/verification/crypto.ts`).
 *  - sandboxed: bounded Web Worker structural inspection of archives /
 *  HTML / documents (see `src/verification/sandbox-worker.ts`).
 *
 * The mode threads into `analyzeEmail` via the Provenance builder so
 * every downstream consumer can read which mode produced a result.
 */

import { useCallback } from "react";
import type { VerificationMode } from "../parser-core/provenance";
import { STORAGE_KEYS } from "../utils/storageKeys";
import { useLocalStorage } from "./useLocalStorage";

export const VERIFICATION_MODES: readonly VerificationMode[] = [
  "parse-only",
  "dns-enabled",
  "crypto-enabled",
  "sandboxed",
] as const;

function isVerificationMode(v: unknown): v is VerificationMode {
  return (
    v === "parse-only" ||
    v === "dns-enabled" ||
    v === "crypto-enabled" ||
    v === "sandboxed"
  );
}

export function useVerificationMode(): {
  mode: VerificationMode;
  setMode: (m: VerificationMode) => void;
  modes: readonly VerificationMode[];
} {
  const [mode, setModeState] = useLocalStorage<VerificationMode>(
    STORAGE_KEYS.verificationMode,
    "parse-only",
    {
      parse: (raw) => (isVerificationMode(raw) ? raw : "parse-only"),
      stringify: (v) => v,
    },
  );
  const setMode = useCallback(
    (next: VerificationMode) => setModeState(next),
    [setModeState],
  );
  return { mode, setMode, modes: VERIFICATION_MODES };
}
